import { observer } from "mobx-react-lite"
import { makeStyles, mergeClasses } from '@griffel/react'
import { Title3 } from '@fluentui/react-text'
import { ButtonProps } from "@fluentui/react-button"
import { tokens } from "@fluentui/tokens"
import { GenericCudList, GenericList } from "@knowmax/genericlist-core"
import { CreateItem, RefreshList } from "."
import { LayoutSelector } from "./LayoutSelector"
import { TListLayout } from "./List"

type IHeadPanelSharedProps<T = unknown, ET = unknown> = {
  /** Title to display in head panel. */
  title?: string,
  /** Optional children to render between optional title and create item. */
  children?: React.ReactNode,
  /** Set to show a refresh button on the right side of head panel. Only when @see list is set. */
  showRefreshList?: boolean,
  /** Props for optional refresh list button. */
  refreshListProps?: ButtonProps,
  /** Optional text to display for optional refresh list button. */
  refreshListText?: string,

  /** Optional class name to merge with default class. */
  className?: string;
  /**
   * Set to show a layout selector on the right side of the head panel. Defaults to false.
   * Requires @see layout and @see onLayoutChange to be set.
   */
  showLayoutSelector?: boolean,
  /** Currently active layout. Required when @see showLayoutSelector is true. */
  layout?: TListLayout,
  /** Callback fired when the user selects a different layout. Required when @see showLayoutSelector is true. */
  onLayoutChange?: (layout: TListLayout) => void,
  /** Restrict the layouts offered in the selector. Defaults to all four layouts. */
  availableLayouts?: TListLayout[],
}

type IHeadPanelRefreshOnlyProps<T = unknown, ET = unknown> = IHeadPanelSharedProps<T, ET> & {
  /** List associated with head panel, used for refresh functionality. */
  list?: GenericList<T>
  /** Set to show create item button on the right side of head panel. */
  showCreateItem?: false,
  /** Create item props are only supported when list supports create(). */
  createItemProps?: never,
  /** Create item text is only supported when list supports create(). */
  createItemText?: never,

}

type IHeadPanelCudProps<T = unknown, ET = unknown> = IHeadPanelSharedProps<T, ET> & {
  /** List associated with head panel, needed to show create item. */
  list?: GenericCudList<T, ET>
  /** Set to show create item button on the right side of head panel. Only when @see list supports create(). */
  showCreateItem?: boolean,
  /** Props for optional create item. */
  createItemProps?: ButtonProps & { template?: ET },
  /** Optional text to display for optional create item. */
  createItemText?: string,

}

type IHeadPanelProps<T = unknown, ET = unknown> = IHeadPanelRefreshOnlyProps<T, ET> | IHeadPanelCudProps<T, ET>

const isCudList = <T = unknown, ET = unknown>(list?: GenericList<T> | GenericCudList<T, ET>): list is GenericCudList<T, ET> => {
  return !!list && typeof (list as GenericCudList<T, ET>).create === 'function'
}

const useClasses = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'row',
    gap: tokens.spacingHorizontalL,
    alignItems: 'end'
  },
  far: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'row',
    gap: tokens.spacingHorizontalXS
  }
})

/** Use to put a fancy head panel above list. For example containing list title, custom contents and create button. */
export const HeadPanel = observer(<T = unknown, ET = unknown>(props: IHeadPanelProps<T, ET>) => {
  const classes = useClasses()
  const canShowRefreshList = !!(props.showRefreshList && props.list)
  const canShowCreateItem = !!(props.showCreateItem && isCudList(props.list))
  const refreshListChildren = props.refreshListProps?.children ?? props.refreshListText
  const createItemChildren = props.createItemProps?.children ?? props.createItemText

  const showFar = (canShowCreateItem || canShowRefreshList)
    || (props.showLayoutSelector && props.layout !== undefined && props.onLayoutChange !== undefined)

  return (
    <div className={mergeClasses(classes.root, props.className)}>
      {props.title &&
        <Title3>{props.title}</Title3>
      }

      {props.children}

      {showFar &&
        <div className={classes.far}>
          {props.showLayoutSelector && props.layout !== undefined && props.onLayoutChange &&
            <LayoutSelector
              value={props.layout}
              onChange={props.onLayoutChange}
              availableLayouts={props.availableLayouts}
            />
          }
          {canShowRefreshList && props.list &&
            <RefreshList list={props.list} {...props.refreshListProps}>{refreshListChildren}</RefreshList>
          }
          {canShowCreateItem && props.list &&
            <CreateItem list={props.list} {...props.createItemProps}>{createItemChildren}</CreateItem>
          }
        </div>
      }
    </div>
  )
})